import { Link } from 'react-router';
import { ArticleLayout, H2, H3, P, UL, LI, Callout } from './ArticleLayout';

export function ShadowAi() {
  return (
    <ArticleLayout
      currentPath="/shadow-ai"
      breadcrumb="Shadow AI"
      eyebrow="Guide"
      title={
        <>
          Shadow AI: the hidden risk, and how to <span className="text-red-accent">control it</span>
        </>
      }
      lead="Shadow AI is the use of AI tools that IT and security have not sanctioned — usually through personal accounts, with real company data. It is the fastest-growing form of shadow IT, and the leading cause of data leakage into generative AI."
    >
      <H2 id="what-is-shadow-ai">What is shadow AI?</H2>
      <P>
        Shadow AI describes any AI tool being used inside an organisation without approval, oversight or
        a data-processing agreement. A developer using a personal ChatGPT account to debug production
        code, a marketer running customer lists through a free summariser, an analyst pasting a finance
        spreadsheet into an AI assistant — all of it is shadow AI. It is well-intentioned: people are
        trying to work faster. But it moves sensitive data outside every control you have.
      </P>

      <H2 id="why-it-happens">Why it happens</H2>
      <UL>
        <LI>AI tools are free, instant, and one browser tab away — there is no procurement friction.</LI>
        <LI>The productivity gains are genuine, so employees adopt faster than policy can keep up.</LI>
        <LI>Personal accounts sit entirely outside corporate SSO, logging and network monitoring.</LI>
        <LI>Most staff do not realise a prompt can be logged, cached or used to train a model.</LI>
      </UL>

      <H2 id="the-risks">The risks of shadow AI</H2>
      <P>
        The core problem is loss of control over data you are accountable for. Concretely, that shows up
        as:
      </P>
      <UL>
        <LI>
          <strong className="text-text-primary">Data leakage.</strong> PII, payment data, credentials
          and source code leave the organisation and cannot be recalled.
        </LI>
        <LI>
          <strong className="text-text-primary">Compliance breaches.</strong> Feeding personal data to
          an unapproved processor can violate GDPR and the UK DPA 2018 — potentially a reportable
          incident.
        </LI>
        <LI>
          <strong className="text-text-primary">IP loss.</strong> Proprietary code and strategy pasted
          into a model may resurface or leak competitive advantage.
        </LI>
        <LI>
          <strong className="text-text-primary">No audit trail.</strong> Because it happens off-network
          and off-account, you cannot prove what did or did not leave.
        </LI>
      </UL>

      <Callout title="The visibility gap">
        <P>
          The defining feature of shadow AI is that you cannot see it. Network and email DLP watch
          different channels, and personal accounts bypass SSO entirely. That is exactly why endpoint
          detection matters — it observes the content at the device, regardless of app or account.
        </P>
      </Callout>

      <H2 id="how-to-control-it">How to control shadow AI</H2>
      <H3>Don&apos;t just ban it</H3>
      <P>
        Outright bans fail. They push usage onto personal phones and home machines where you have no
        visibility at all, and they cost you the productivity your competitors are keeping. The goal is
        safe enablement, not prohibition.
      </P>
      <H3>Gain visibility first</H3>
      <P>
        You cannot manage what you cannot measure. Start by monitoring which AI tools are in use and
        what categories of data flow to them. Endpoint{' '}
        <Link to="/ai-dlp" className="text-red-accent underline underline-offset-4 hover:text-red-hover">
          AI DLP
        </Link>{' '}
        gives you that picture without routing traffic through a cloud gateway — inspection happens on
        the device.
      </P>
      <H3>Apply graduated policy</H3>
      <P>
        With visibility, set proportionate rules: block the crown jewels (secrets, source code, bulk
        PII), warn on medium-risk content so employees can make an informed choice, and allow ordinary
        use. This keeps AI available while closing the leak. The{' '}
        <Link
          to="/stop-chatgpt-data-leaks"
          className="text-red-accent underline underline-offset-4 hover:text-red-hover"
        >
          step-by-step guide to stopping ChatGPT data leaks
        </Link>{' '}
        covers the rollout in detail.
      </P>

      <P>
        Redbax turns shadow AI from an invisible risk into a managed one — real-time detection on
        Windows and Linux, across major AI services like ChatGPT, Copilot and Claude.{' '}
        <Link to="/contact" className="text-red-accent underline underline-offset-4 hover:text-red-hover">
          Register your interest
        </Link>{' '}
        to learn more.
      </P>
    </ArticleLayout>
  );
}
